import * as THREE from 'three'

export type FootPlantSemantic='walk'|'backward'|'run'|'dash'|'depth'
export type FootPlantSide='left'|'right'
export type FootPlantReport={
 active:boolean
 side:FootPlantSide|null
 errorBefore:number
 errorAfter:number
 correction:number
 validChains:number
}

type Chain={
 effector:THREE.Object3D
 foot:THREE.Object3D
 lower:THREE.Bone
 upper:THREE.Bone
}

function boneParent(node:THREE.Object3D|null|undefined){
 let current=node?.parent??null
 while(current){
  if((current as THREE.Bone).isBone)return current as THREE.Bone
  current=current.parent
 }
 return null
}

function chainFor(foot:THREE.Object3D|null|undefined,effector:THREE.Object3D|null|undefined):Chain|null{
 if(!foot||!effector)return null
 const lower=boneParent(foot)
 const upper=boneParent(lower)
 if(!lower||!upper||lower===upper)return null
 return{effector,foot,lower,upper}
}

const jointWorld=new THREE.Vector3()
const effectorWorld=new THREE.Vector3()
const from=new THREE.Vector3()
const to=new THREE.Vector3()
const currentWorldQ=new THREE.Quaternion()
const parentWorldQ=new THREE.Quaternion()
const desiredWorldQ=new THREE.Quaternion()
const desiredLocalQ=new THREE.Quaternion()
const deltaWorldQ=new THREE.Quaternion()
const targetWorld=new THREE.Vector3()

function rotateJointToward(joint:THREE.Bone,effector:THREE.Object3D,target:THREE.Vector3,maxAngle:number,strength:number){
 joint.getWorldPosition(jointWorld)
 effector.getWorldPosition(effectorWorld)
 from.copy(effectorWorld).sub(jointWorld)
 to.copy(target).sub(jointWorld)
 if(from.lengthSq()<1e-8||to.lengthSq()<1e-8)return
 from.normalize();to.normalize()
 deltaWorldQ.setFromUnitVectors(from,to)
 joint.getWorldQuaternion(currentWorldQ)
 desiredWorldQ.copy(deltaWorldQ).multiply(currentWorldQ)
 if(joint.parent){
  joint.parent.getWorldQuaternion(parentWorldQ)
  desiredLocalQ.copy(parentWorldQ).invert().multiply(desiredWorldQ)
 }else desiredLocalQ.copy(desiredWorldQ)
 const angle=joint.quaternion.angleTo(desiredLocalQ)
 if(!Number.isFinite(angle)||angle<1e-5)return
 const allowed=Math.min(angle,Math.max(.001,maxAngle))*THREE.MathUtils.clamp(strength,0,1)
 joint.quaternion.rotateTowards(desiredLocalQ,allowed)
 joint.updateMatrix()
 joint.updateMatrixWorld(true)
}

export class FootPlantRuntime{
 private readonly left:Chain|null
 private readonly right:Chain|null
 private side:FootPlantSide|null=null
 private readonly target=new THREE.Vector3()
 private age=0
 private readonly leftWorld=new THREE.Vector3()
 private readonly rightWorld=new THREE.Vector3()
 private readonly resultWorld=new THREE.Vector3()

 constructor(nodes:{
  foot_l:THREE.Object3D|null|undefined
  foot_r:THREE.Object3D|null|undefined
  toe_l?:THREE.Object3D|null|undefined
  toe_r?:THREE.Object3D|null|undefined
 }){
  this.left=chainFor(nodes.foot_l,nodes.toe_l??nodes.foot_l)
  this.right=chainFor(nodes.foot_r,nodes.toe_r??nodes.foot_r)
 }

 get validChains(){return Number(Boolean(this.left))+Number(Boolean(this.right))}

 private chain(side:FootPlantSide){return side==='left'?this.left:this.right}

 private position(side:FootPlantSide,out:THREE.Vector3){
  const chain=this.chain(side)
  if(!chain)return null
  chain.effector.getWorldPosition(out)
  return out
 }

 private choose(){
  const left=this.position('left',this.leftWorld),right=this.position('right',this.rightWorld)
  if(!left&&right)return'right' as const
  if(left&&!right)return'left' as const
  if(!left||!right)return null
  if(this.side==='left'&&right.y<left.y-.035)return'right' as const
  if(this.side==='right'&&left.y<right.y-.035)return'left' as const
  return this.side??(left.y<=right.y?'left':'right')
 }

 private replant(side:FootPlantSide){
  const current=this.position(side,side==='left'?this.leftWorld:this.rightWorld)
  if(!current)return false
  this.side=side
  this.target.copy(current)
  this.age=0
  return true
 }

 update(input:{
  dt:number
  semantic:string
  grounded:boolean
  contactPrecision:number
 }):FootPlantReport{
  const locomotion=(['walk','backward','run','dash','depth'] as string[]).includes(input.semantic)
  if(!locomotion||!input.grounded||this.validChains<1){
   this.side=null;this.age=0
   return{active:false,side:null,errorBefore:0,errorAfter:0,correction:0,validChains:this.validChains}
  }

  const candidate=this.choose()
  if(!candidate){
   return{active:false,side:null,errorBefore:0,errorAfter:0,correction:0,validChains:this.validChains}
  }
  if(this.side!==candidate||!this.chain(candidate)){
   if(!this.replant(candidate))return{active:false,side:null,errorBefore:0,errorAfter:0,correction:0,validChains:this.validChains}
  }

  this.age+=Math.max(0,input.dt)
  const current=this.position(this.side!,this.side==='left'?this.leftWorld:this.rightWorld)
  const chain=this.chain(this.side!)
  if(!current||!chain)return{active:false,side:null,errorBefore:0,errorAfter:0,correction:0,validChains:this.validChains}

  const dx=this.target.x-current.x,dz=this.target.z-current.z
  const errorBefore=Math.hypot(dx,dz)
  const maxReach=input.semantic==='dash'?.88:input.semantic==='run'?.72:.6
  if(errorBefore>maxReach||this.age>.46){
   this.replant(this.side!)
   return{active:true,side:this.side,errorBefore, errorAfter:0,correction:0,validChains:this.validChains}
  }

  targetWorld.copy(current)
  targetWorld.x=this.target.x
  targetWorld.z=this.target.z

  const precision=.52+.48*THREE.MathUtils.clamp(input.contactPrecision,0,1)
  const angularSpeed=input.semantic==='dash'?10:input.semantic==='run'?8.6:7.8
  const maxAngle=Math.max(.015,Math.min(.2,angularSpeed*Math.max(0,input.dt)))

  for(let iteration=0;iteration<2;iteration++){
   rotateJointToward(chain.upper,chain.effector,targetWorld,maxAngle,precision*.72)
   rotateJointToward(chain.lower,chain.effector,targetWorld,maxAngle,precision)
  }

  chain.effector.getWorldPosition(this.resultWorld)
  const errorAfter=Math.hypot(this.target.x-this.resultWorld.x,this.target.z-this.resultWorld.z)
  return{
   active:true,
   side:this.side,
   errorBefore,
   errorAfter,
   correction:Math.max(0,errorBefore-errorAfter),
   validChains:this.validChains,
  }
 }
}
